/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * The Ops Console detail page parts: underline tabs (brand-500), white section cards with a title,
 * and key/value grids (label 12 px slate-500, value 14 px slate-900).
 */

import * as React from "react";
import { Link } from "react-router";
import styled, { css } from "styled-components";
import type { LucideIcon } from "lucide-react";

import { T9 } from "@src/components/ui/Lists/ListTable";

// ------------------------------------------------------------------- page

/** The body of a detail page: tabs on top, then the cards. */
export const DetailsPage = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  min-width: 0;
  padding-bottom: 24px;
`;
export const DetailsColumns = styled.div<{ columns?: string }>`
  display: grid;
  grid-template-columns: ${props => props.columns || "minmax(0, 1fr) minmax(0, 1fr)"};
  gap: 16px;
  align-items: start;
  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

// ------------------------------------------------------------------- tabs

const TabsWrapper = styled.div`
  display: flex;
  gap: 4px;
  border-bottom: 1px solid ${T9.slate200};
  overflow-x: auto;
  flex-shrink: 0;
`;
const tabStyle = css<{ selected?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  margin-bottom: -1px;
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
  text-decoration: none;
  border-bottom: 2px solid ${props => (props.selected ? T9.brand500 : "transparent")};
  color: ${props => (props.selected ? T9.brand700 : T9.slate500)};
  font-weight: ${props => (props.selected ? 500 : 400)};
  cursor: pointer;
  &:hover {
    color: ${props => (props.selected ? T9.brand700 : T9.slate800)};
  }
`;
const TabLink = styled(Link)<{ selected?: boolean }>`
  ${tabStyle}
`;
const TabButton = styled.button<{ selected?: boolean }>`
  ${tabStyle}
  background: none;
  border-top: 0;
  border-left: 0;
  border-right: 0;
  font-family: inherit;
`;
const TabCount = styled.span`
  font-size: 11px;
  font-weight: 500;
  padding: 0 6px;
  border-radius: 9999px;
  background: ${T9.slate100};
  color: ${T9.slate600};
`;

export type DetailsTab = { label: string; value: string; count?: number; icon?: LucideIcon };
type TabsProps = {
  items: DetailsTab[];
  selectedValue?: string;
  /** Link tabs: the URL of each tab. */
  href?: (item: DetailsTab) => string;
  /** Button tabs (no URL). */
  onChange?: (value: string) => void;
};
export const DetailsTabs = ({ items, selectedValue, href, onChange }: TabsProps) => (
  <TabsWrapper role="tablist" data-test-id="detailsTabs">
    {items.map(item => {
      const selected = item.value === (selectedValue || "");
      const Icon = item.icon;
      const content = (
        <>
          {Icon ? <Icon size={14} /> : null}
          {item.label}
          {item.count !== undefined ? <TabCount>{item.count}</TabCount> : null}
        </>
      );
      return href ? (
        <TabLink
          key={item.value || item.label}
          to={href(item)}
          selected={selected}
          role="tab"
          aria-selected={selected}
        >
          {content}
        </TabLink>
      ) : (
        <TabButton
          key={item.value || item.label}
          type="button"
          selected={selected}
          role="tab"
          aria-selected={selected}
          onClick={() => onChange?.(item.value)}
        >
          {content}
        </TabButton>
      );
    })}
  </TabsWrapper>
);

// ------------------------------------------------------------------ cards

const CardWrapper = styled.section`
  background: var(--t9-bg-surface);
  border: 1px solid ${T9.slate200};
  border-radius: 8px;
  min-width: 0;
`;
const CardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 20px 0 20px;
`;
const CardTitle = styled.h2`
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  color: ${T9.slate800};
  svg {
    color: ${T9.slate400};
  }
`;
const CardSubtitle = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: ${T9.slate500};
  margin-top: 2px;
`;
const CardBody = styled.div<{ flush?: boolean }>`
  padding: ${props => (props.flush ? "12px 0 0 0" : "14px 20px 18px 20px")};
`;

type CardProps = {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  icon?: LucideIcon;
  actions?: React.ReactNode;
  /** No inner padding (for a table that runs edge to edge). */
  flush?: boolean;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  testId?: string;
};
export const SectionCard = (props: CardProps) => {
  const Icon = props.icon;
  return (
    <CardWrapper className={props.className} style={props.style} data-test-id={props.testId || "sectionCard"}>
      {props.title || props.actions ? (
        <CardHead>
          <div style={{ minWidth: 0 }}>
            {props.title ? (
              <CardTitle>
                {Icon ? <Icon size={16} /> : null}
                {props.title}
              </CardTitle>
            ) : null}
            {props.subtitle ? <CardSubtitle>{props.subtitle}</CardSubtitle> : null}
          </div>
          {props.actions ? <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>{props.actions}</div> : null}
        </CardHead>
      ) : null}
      <CardBody flush={props.flush}>{props.children}</CardBody>
    </CardWrapper>
  );
};

// -------------------------------------------------------------- key/value

const Grid = styled.dl<{ columns: number }>`
  display: grid;
  grid-template-columns: repeat(${props => props.columns}, minmax(0, 1fr));
  gap: 14px 24px;
  margin: 0;
`;
const Pair = styled.div<{ span?: number }>`
  min-width: 0;
  ${props => (props.span ? `grid-column: span ${props.span};` : "")}
`;
export const KvLabel = styled.dt`
  font-size: 12px;
  line-height: 16px;
  color: ${T9.slate500};
  margin: 0 0 2px 0;
  display: flex;
  align-items: center;
  gap: 4px;
`;
export const KvValue = styled.dd<{ mono?: boolean }>`
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: ${T9.slate900};
  overflow-wrap: anywhere;
  ${props =>
    props.mono
      ? css`
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
          font-size: 13px;
        `
      : ""}
`;
export const KvEmpty = styled.span`
  color: ${T9.slate400};
`;

export type KvItem = {
  label: React.ReactNode;
  value: React.ReactNode;
  mono?: boolean;
  /** Columns the pair takes (default 1). */
  span?: number;
  hidden?: boolean;
  testId?: string;
};
type GridProps = { items: KvItem[]; columns?: number };
export const KeyValueGrid = ({ items, columns = 2 }: GridProps) => (
  <Grid columns={columns} data-test-id="keyValueGrid">
    {items
      .filter(i => !i.hidden)
      .map((item, i) => (
        <Pair key={typeof item.label === "string" ? item.label : i} span={item.span} data-test-id={item.testId}>
          <KvLabel>{item.label}</KvLabel>
          <KvValue mono={item.mono}>
            {item.value === null || item.value === undefined || item.value === "" ? <KvEmpty>—</KvEmpty> : item.value}
          </KvValue>
        </Pair>
      ))}
  </Grid>
);

/** A bordered list inside a card (users of a project, transfers of an endpoint...). */
export const CardList = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid ${T9.slate100};
  margin: 0 -20px -18px -20px;
`;
export const CardListRow = styled.div<{ clickable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  padding: 6px 20px;
  border-bottom: 1px solid ${T9.slate100};
  font-size: 13px;
  color: ${T9.slate700};
  ${props =>
    props.clickable
      ? css`
          cursor: pointer;
          &:hover {
            background: ${T9.slate50};
          }
        `
      : ""}
  &:last-child {
    border-bottom: 0;
  }
`;

/** Buttons row at the bottom of a detail page. */
export const DetailsActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
`;
export const ActionGroup = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

/** Ops buttons: primary (brand fill), secondary (white, slate border), danger (soft red). */
export const ActionButton = styled.button<{ variant?: "primary" | "secondary" | "danger" }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  border-radius: 6px;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  border: 1px solid
    ${props => (props.variant === "secondary" ? T9.slate300 : "transparent")};
  background: ${props =>
    props.variant === "secondary" ? "var(--t9-bg-surface)" : props.variant === "danger" ? "var(--t9-danger-50)" : T9.brand600};
  color: ${props =>
    props.variant === "secondary" ? T9.slate700 : props.variant === "danger" ? "var(--t9-danger-700)" : "var(--t9-text-on-fill)"};
  &:hover:not(:disabled) {
    background: ${props =>
      props.variant === "secondary" ? T9.slate50 : props.variant === "danger" ? "var(--t9-danger-200)" : T9.brand700};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 2px solid ${T9.accent500};
    outline-offset: 2px;
  }
  .spin {
    animation: dk-spin 1s linear infinite;
  }
  @keyframes dk-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
